/**
 * InquiryListPresenter
 * --
 */
import React, { useState } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Pagination,
  HtmlContent,
  ModalLayout,
  InputFormTable,
  ShopInformationList,
} from 'components';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Tag,
  Spin,
  Divider,
  Radio,
  Flex,
  Table,
} from 'antd';
import moment from 'moment';
import { strCut } from 'utils';
import InquiryDetail from '../InquiryDetail';
import { InquirySubType } from 'constants/InquirySubType';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';

const INITIAL_VALUE = {
  start: '',
  end: '',
  keyword: 'inquiry_name',
  search: '',
  type: '',
  fixed: '',
  status: '',
  is_secret: '',
};

/**----------------------------------------
 * [Component] 총관리자: 문의목록 - 프레젠터
 * ----------------------------------------
 */
const InquiryListPresenter = ({
  FAQ,
  loading,
  data,
  permission,
  currentShop,
  setCurrentShop,
  onSavedInquiry,
  onDeletedInquiry,
  setLoading,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [isOpened, setIsOpened] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modalDetail, setModalDetail] = useState({});
  const [filterItem, setFilterItem] = React.useState(INITIAL_VALUE);
  const [filterList, setFilterList] = React.useState([...data]);

  // console.log('data ====>', data);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 150,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };
  /**
   * 필터 아이템 및 문의 유형 정제
   * ---
   */
  const FAQCategories = React.useMemo(() => {
    if (FAQ && filterList) {
      return filterList?.reduce((acc, inquiry) => {
        const name = inquiry.inquiry_name;
        if (name && !acc.find((item) => item.value === name)) {
          acc.push({ label: name, value: name, text: name });
        }
        return acc;
      }, []);
    }
    return [
      {
        label: '주문/결제',
        text: '주문/결제',
        value: '주문/결제',
      },
      {
        label: '상품',
        text: '상품',
        value: '상품',
      },
      {
        label: '배송',
        text: '배송',
        value: '배송',
      },
      {
        label: '교환/환불',
        text: '교환/환불',
        value: '교환/환불',
      },
      {
        label: '회원가입/로그인',
        text: '상회원가입/로그인품',
        value: '회원가입/로그인',
      },
      {
        label: '스탬프/쿠폰/이벤트',
        text: '스탬프/쿠폰/이벤트',
        value: '스탬프/쿠폰/이벤트',
      },
      {
        label: '기타',
        text: '기타',
        value: '기타',
      },
    ];
  }, [FAQ, filterList]);

  const formItem = [
    {
      label: '문의 작성일자',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                onChange={(e) => {
                  setFilterItem((prev) => ({
                    ...prev,
                    start: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end: dayjs().endOf('day'),
                  }));
                }}
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>

              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date }}
                  value={filterItem.start ? dayjs(filterItem.start) : ''}
                  onChange={(e) =>
                    setFilterItem((prev) => ({ ...prev, start: e }))
                  }
                  maxDate={filterItem.end}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date }}
                  value={filterItem.end ? dayjs(filterItem.end) : ''}
                  onChange={(e) =>
                    setFilterItem((prev) => ({ ...prev, end: e }))
                  }
                  minDate={filterItem.start}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '검색어',
      direction: 'column',
      form: [
        {
          input: (
            <>
              <Select
                size="large"
                value={filterItem.keyword}
                options={[
                  { label: '문의 명', value: 'inquiry_name' },
                  { label: '문의 내용', value: 'inquiry_content' },
                  { label: '상품 번호', value: 'product_code' },
                  { label: '상품 명', value: 'product_name' },
                  { label: '문의자 명', value: 'user_name' },
                ]}
                style={{ ...styles.form.input.large }}
              />
              <Input
                size="large"
                placeholder="검색어를 입력해주세요"
                style={{ ...styles.form.input.large, width: 350 }}
                value={filterItem.search}
                onChange={(e) =>
                  setFilterItem((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '문의 유형',
      form: [
        {
          input: (
            <Flex vertical gap="large" size="large">
              <Radio.Group
                value={filterItem.type}
                size="large"
                onChange={(e) =>
                  setFilterItem((prev) => ({ ...prev, type: e.target.value }))
                }
              >
                <Radio.Button value="">전체</Radio.Button>
                {/* 1:1 */}
                <Radio.Button value="SERVICE">서비스</Radio.Button>
                <Radio.Button value="USER">회원정보</Radio.Button>
                <Radio.Button value="JM_PAY">제이엠페이</Radio.Button>
                <Radio.Button value="JM_PAY_REFUND">환불신청</Radio.Button>
                {/* 공통 */}
                <Radio.Button value="DELIVERY">배송</Radio.Button>
                <Radio.Button value="BULK">대량문의</Radio.Button>
                <Radio.Button value="ORDER">주문/결제</Radio.Button>
                <Radio.Button value="ETC">기타</Radio.Button>
                <Radio.Button value="STORE">매장</Radio.Button>
                {/* 상품관련 */}
                <Radio.Button value="RESTOCK">재입고</Radio.Button>
                <Radio.Button value="DETAIL">상세</Radio.Button>
              </Radio.Group>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '답변상태',
      form: [
        {
          input: (
            <Flex vertical gap="large" size="large">
              <Radio.Group
                value={filterItem.status}
                size="large"
                onChange={(e) =>
                  setFilterItem((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <Radio value={''}>전체</Radio>
                <Radio value={'COMPLETE'}>답변완료</Radio>
                <Radio value={'WAIT'}>답변대기</Radio>
              </Radio.Group>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '비밀글여부',
      form: [
        {
          input: (
            <Flex vertical gap="large" size="large">
              <Radio.Group
                value={filterItem.is_secret}
                size="large"
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    is_secret: e.target.value,
                  }))
                }
              >
                <Radio value={''}>전체</Radio>
                <Radio value={'Y'}>O</Radio>
                <Radio value={'N'}>X</Radio>
              </Radio.Group>
            </Flex>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  /**
   * 문의 모달 핸들러
   * --
   */
  const handleInquiryModal = (d) => {
    setIsOpened(!isOpened);
    setModalData(d);
  };

  /**
   * 문의 조건 검색
   * --
   * @param {string} t
   */
  const handleSearch = (t) => {
    setLoading(true);
    const searchType = t.toLocaleUpperCase();

    let arr = [...data];
    switch (searchType) {
      case 'RESET':
        setFilterItem(INITIAL_VALUE);
        break;
      case 'SEARCH':
        const { start, end, keyword, search, type, status, is_secret } =
          filterItem;
        if (search) {
          arr = arr.filter((inquiry) => {
            switch (keyword) {
              case 'user_name':
                return inquiry.user[keyword].includes(search);
              case 'product_code':
              case 'product_name':
                return inquiry.product[keyword].includes(search);
              default:
                return inquiry[keyword].includes(search);
            }
          });
        }

        if (type) {
          arr = arr.filter((inquiry) => inquiry.inquiry_sub_type === type);
        }

        if (status) {
          arr = arr.filter((inquiry) => {
            if (status === 'COMPLETE') {
              return inquiry.inquiry_answer;
            } else {
              return !inquiry.inquiry_answer;
            }
          });
        }

        if (is_secret) {
          arr = arr.filter((inquiry) => inquiry.is_secret === is_secret);
        }

        if (start || end) {
          const filterType = start && end ? 'BETWEEN' : start ? 'START' : 'END';

          switch (filterType) {
            case 'START':
              arr = arr.filter(
                (inquiry) =>
                  inquiry.created_at >= dayjs(start).startOf('day').unix()
              );
              break;
            case 'END':
              arr = arr.filter(
                (inquiry) =>
                  inquiry.created_at <= dayjs(end).endOf('day').unix()
              );
              break;
            case 'BETWEEN':
              arr = arr.filter(
                (inquiry) =>
                  inquiry.created_at >= dayjs(start).startOf('day').unix() &&
                  inquiry.created_at <= dayjs(end).endOf('day').unix()
              );
              break;
            default:
              break;
          }
        }

        break;
      default:
        break;
    }

    setTimeout(() => {
      setLoading(false);
      setFilterList(arr);
    }, 1000);
  };

  /* ===== HOOKS ===== */
  React.useEffect(() => {
    setFilterList(data?.sort((a, b) => b.created_at - a.created_at));
  }, [data]);

  const columns = React.useMemo(() => {
    return {
      true: [
        {
          title: 'FAQ 유형',
          key: 'inquiry_name',
          dataIndex: 'inquiry_name',
          width: 110,
          render: (value) => <Tag>{value}</Tag>,
          filters: FAQCategories,
          onFilter: (v, { inquiry_name }) => inquiry_name.includes(v),
        },
        {
          title: '작성일자',
          key: 'created_at',
          dataIndex: 'created_at',
          width: 200,
          render: (item) => moment(item * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
          sorter: (a, b) => a.created_at - b.created_at,
        },
        {
          title: 'FAQ 내용',
          key: 'inquiry_content',
          dataIndex: 'inquiry_content',
          // width: 280,
          render: (item) => (
            <div>
              <HtmlContent
                content={strCut(item, 35)}
                style={{
                  display: 'flex',
                  padding: '0px 5px',
                  overflow: 'hidden',
                }}
              />
            </div>
          ),
          style: { maxWidth: 500, padding: 8 },
        },
        {
          title: 'FAQ 답변',
          key: 'inquiry_answer',
          dataIndex: 'inquiry_answer',
          // width: 280,
          render: (item) => (
            <div>
              <HtmlContent
                content={strCut(item, 35)}
                style={{
                  display: 'flex',
                  padding: '0px 5px',
                  overflow: 'hidden',
                }}
              />
            </div>
          ),
          style: { maxWidth: 500, padding: 8 },
        },
      ],
      false: [
        {
          title: '문의유형',
          key: 'inquiry_sub_type',
          dataIndex: 'inquiry_sub_type',
          width: 100,
          render: (value) => <Tag>{InquirySubType[value]?.label}</Tag>,
        },
        {
          title: '작성일자',
          key: 'created_at',
          dataIndex: 'created_at',
          width: 200,
          render: (item) => moment(item * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
          sorter: (a, b) => a.created_at - b.created_at,
        },
        {
          title: '문의명',
          key: 'inquiry_name',
          dataIndex: 'inquiry_name',
          width: 220,
          // align: 'left',
          render: (item) => strCut(item, 20),
        },
        {
          title: '문의내용',
          key: 'inquiry_content',
          dataIndex: 'inquiry_content',
          // width: 280,
          render: (item) => (
            <div>
              <HtmlContent
                content={strCut(item, 35)}
                style={{
                  display: 'flex',
                  padding: '0px 5px',
                  overflow: 'hidden',
                }}
              />
            </div>
          ),
          style: { maxWidth: 500, padding: 8 },
        },

        ...(FAQ === true
          ? []
          : [
              {
                title: '작성자',
                key: 'user',
                dataIndex: 'user',
                width: 100,
                // align: 'left',
                render: (item) => strCut(item?.user_name, 20),
              },
              {
                title: '답변여부',
                key: 'inquiry_answer',
                dataIndex: 'inquiry_answer',
                width: 80,
                render: (item) => {
                  const isAnswer = item === null ? false : true;
                  return (
                    <Tag color={isAnswer ? 'success' : 'default'}>
                      {isAnswer ? '답변 완료' : '답변 대기'}
                    </Tag>
                  );
                },
              },
              {
                title: '비밀글',
                key: 'is_secret',
                dataIndex: 'is_secret',
                width: 80,
                render: (item) => (item === 'Y' ? 'O' : 'X'),
              },
            ]),
      ],
    };
  }, [FAQ, FAQCategories]);

  /* ===== RENDER ===== */
  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            {/* ==== 상단 ==== */}
            <Col x={24}>
              <Title
                size={3}
                style={{ marginBottom: 12 }}
                subButton={
                  FAQ === true ? (
                    <Button onClick={() => setIsOpened(true)}>+FAQ생성</Button>
                  ) : null
                }
              >
                {FAQ === true ? 'FAQ 목록' : '문의 목록'}
              </Title>

              {/* === 검색필터: start === */}
              {FAQ === false && (
                <Content
                  maxWidth={'100%'}
                  padding={0}
                  backgroundColor={'none'}
                  style={{ marginBottom: 20 }}
                >
                  <ExpandableContainer borderRadius={4} padding={20}>
                    {formItem.map((_item, _index) => (
                      <div
                        key={`form_${_index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 0px',
                        }}
                      >
                        <div
                          style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                        >
                          <label>{_item.label}</label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {_item.form.map((_subitem, _subkey) => (
                            <React.Fragment key={`input_${_subkey}`}>
                              {_subitem.input}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </ExpandableContainer>

                  {/* 검색/초기화 버튼 */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      marginTop: 13,
                    }}
                  >
                    <Button
                      size="large"
                      type="primary"
                      style={{
                        ...styles.button,
                        background: 'var(--primary-color)',
                      }}
                      onClick={() => handleSearch('search')}
                    >
                      검색
                    </Button>
                    <Button
                      size="large"
                      style={{ ...styles.button }}
                      onClick={() => handleSearch('reset')}
                    >
                      초기화
                    </Button>
                  </div>
                  {/* 검색/초기화 버튼 끝 */}
                </Content>
              )}
              {/* === 검색필터: end === */}
            </Col>
          </Row>

          <Row gutter={[1, 1]}>
            {/* === 업체목록: start === */}
            {permission === 'ADMIN' && FAQ === false && (
              <Col x={4}>
                <ShopInformationList maxHeight onChange={setCurrentShop} />
              </Col>
            )}
            {/* === 업체목록: end === */}
            <Col x={permission === 'ADMIN' && FAQ === false ? 20 : 24}>
              {/* === 검색결과: start === */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <h4
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    검색 결과 총 <span>{filterList.length}</span>건
                  </h4>
                  <Button
                    onClick={() => {
                      downloadXlsx({
                        columns: [
                          ...columns[FAQ],
                          {
                            title: '답변',
                            key: 'answer',
                          },
                        ],
                        rows: filterList?.map((_item) => {
                          _item.inquiry_sub_type = `INQUIRY_${_item?.inquiry_sub_type}`;
                          _item.user = _item?.user?.user_name;
                          _item.answer = _item?.inquiry_answer;
                          _item.inquiry_answer = _item?.inquiry_answer
                            ? '답변 완료'
                            : '답변 대기';
                          ///
                          return _item;
                        }),
                        fileName: FAQ === true ? 'FAQ목룍' : '문의목록',
                      });
                    }}
                  >
                    엑셀로 다운로드
                  </Button>
                </div>
                <Table
                  defaultPageSize={20}
                  dataSource={filterList}
                  columns={columns[FAQ]}
                  onRow={(item) => ({
                    onClick: () => handleInquiryModal(item),
                  })}
                />

                {/* <Pagination total={40} style={{ marginTop: 20 }} /> */}
              </Content>
              {/* === 검색결과: end === */}
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* 모달 */}
      <ModalLayout
        type={'modal'}
        open={isOpened}
        onCancel={() => {
          setModalData(null);
          setTimeout(() => setIsOpened(false), 100);
        }}
        // centered
        closable
        title={FAQ ? 'FAQ 상세내용' : `문의 상세내용`}
        width={798}
      >
        <Divider style={{ margin: '13px 0' }} />
        {/* <Content
          maxWidth={'100%'}
          padding={'20px 0px'}
          style={{
            borderTop: '1px solid #F4F4F5',
            borderBottom: '1px solid #F4F4F5',
          }}
        >
          <Row>
            <Col x={24}>
              <InputFormTable form={form} />
            </Col>
          </Row>
        </Content> */}

        <InquiryDetail
          inquiryId={modalData?.inquiry_id}
          modalData={modalData}
          onSaved={onSavedInquiry}
          setIsOpened={setIsOpened}
          onDeleted={(key) => {
            setModalData({});
            onDeletedInquiry(key);
          }}
          FAQ={FAQ}
          FAQCategories={FAQCategories}
        />
      </ModalLayout>
    </>
  ); // JSX: end
};

export default InquiryListPresenter;
